/*
 * Privacy policy and terms of service for platform18.org and the members app
 * (members.platform18.org).
 *
 * DRAFT FOR LAWYER REVIEW. Written in October 2026 from what the members app
 * actually does (p18-members source) and the membership agreement in the app
 * (content/rules-v1.md). Items marked CONFIRM in the comments are facts or
 * terms a lawyer or Yossi still needs to check. When they change the text,
 * bump `updated` so readers can see the date moved.
 *
 * A block is either a paragraph (string) or a bullet list ({ list }).
 */

export type LegalBlock = string | { list: string[] };
export type LegalSection = { heading: string; body: LegalBlock[] };
export type LegalDoc = { title: string; updated: string; intro: LegalBlock[]; sections: LegalSection[] };

// Yossi, 2026-10-06: the legal entity is Partners Detroit. Used in both
// documents. CONFIRM with the lawyer whether the registered name has a suffix.
const WHO = "Partners Detroit, which runs Platform 18";

export const privacy: LegalDoc = {
  title: "Privacy policy",
  updated: "October 6, 2026",
  intro: [
    `This policy explains what information ${WHO} (“Platform 18”, “we”, “us”) collects through platform18.org and the Platform 18 members app at members.platform18.org, why we collect it, who we share it with, and the choices you have. We don’t sell your information, and we don’t use it for advertising.`,
  ],
  sections: [
    {
      heading: "The public website",
      body: [
        "You can read platform18.org without an account. It sets no cookies and runs no analytics or advertising trackers. Like every website, our hosting provider receives basic technical details such as your IP address and browser type when you visit, and keeps them briefly in server logs for security. The site loads its fonts from Google Fonts, so Google also receives your IP address when a page loads.",
        "If you email or call us, we use what you send only to answer you.",
      ],
    },
    {
      heading: "What the members app collects",
      body: [
        "When you apply for or hold a membership, we collect:",
        {
          list: [
            "Account details: your name, email address, and a sign-in ID from Google or Apple if you sign in with one of them. With Google sign-in we receive only your name, email address, and profile photo. We never see your Google or Apple password, and we don’t access your Gmail, contacts, calendar or files.",
            "If you sign in with email and a password instead: your password, which we store only as a one-way scrambled hash, and your mobile number. We text a 6-digit code to that number each time you sign up, sign in or reset your password. If an admin adds you as a member, they may enter your mobile number so we can text you an invitation. When you choose a password, we check it against a public list of leaked passwords (Have I Been Pwned) using only a short fragment of its hash, so your password never leaves our server.",
            "Your photo, which appears on your member pass. We start with your Google profile photo if you have one, and you can replace it.",
            "The interests you pick when you apply, so we can plan events members want.",
            "Your acceptance of the membership agreement and house rules, with the version and date you accepted.",
            "Billing records: your Stripe customer ID, subscription status, renewal date, and any promotion code you used. Your card details go directly to Stripe. We never see or store your full card number.",
            "Your door pass: your member number and the QR code our door system issues to you. We store the QR code encrypted.",
            "Room bookings: which room, when, and whether you checked in.",
            "Questions you type into Ask, our in-app help button.",
            "Security and device data: passkeys you register (we store only the public key), a sign-in cookie that keeps you signed in, when your email and mobile number were verified, records of sign-in attempts (the email or IP address used, and when) so we can block repeated guessing, and, if you add your pass to Apple Wallet, a device ID and push token so we can update the pass.",
            "A history of account events, such as approvals, payments, pass changes and admin actions, so we can answer questions and resolve problems.",
          ],
        },
      ],
    },
    {
      heading: "The building",
      body: [
        // CONFIRM: which areas are recorded, and how long footage is kept.
        "Our door system records each entry with your member number and the time. The entrance and common areas are recorded on video cameras. The cameras do not record audio. Footage is kept for a limited period and then overwritten, unless we need it to look into an incident or the law requires us to keep it.",
      ],
    },
    {
      heading: "How we use it",
      body: [
        {
          list: [
            "To review your application and run your membership: approving it, charging dues, issuing and revoking your door pass, and handling cancellations.",
            "To let you into the building, keep the door closed on Shabbos and Yom Tov, and keep members and the space safe.",
            "To run room bookings.",
            "To send you messages about your membership, such as approval, receipts, payment problems, pass updates and rule changes.",
            "To answer your questions, including through Ask.",
            "To investigate misuse, such as a shared door pass, and to meet legal, tax and accounting obligations.",
          ],
        },
        "We text you only to send sign-in codes and, if an admin adds you, an invitation. We don’t send marketing texts. Message and data rates may apply.",
        "A person, not an algorithm, decides who is approved. Ask only answers questions from Platform 18’s own information. It never makes decisions about your membership, access or payments.",
      ],
    },
    {
      heading: "Who we share it with",
      body: [
        "We share information only with the service providers who help us run Platform 18, and only what each one needs:",
        {
          list: [
            "Stripe, to process payments.",
            "Google, for Google sign-in, our email, and Google Wallet passes.",
            "Twilio, which texts sign-in codes and invitations. It receives your mobile number and the message.",
            "Mailchimp Transactional (Mandrill), which sends our sign-in codes, sign-in links, password reset links and invitations by email. It receives your email address and the message.",
            "Apple, for Sign in with Apple and Apple Wallet passes.",
            "Railway, which hosts our website and app.",
            "Anthropic, which powers Ask. It receives the question you type, your name, and whether your membership is active, and nothing else about your account.",
          ],
        },
        "Each provider handles your information under its own privacy terms. We may also disclose information if the law requires it, to protect someone’s safety, or as part of a transfer of Platform 18 to another organization, in which case this policy would continue to apply to your information.",
      ],
    },
    {
      heading: "Google user data",
      body: [
        "When you sign in with Google, we use the name, email address and profile photo Google shares only to create and identify your Platform 18 account and to show your photo on your pass. We don’t use Google user data for advertising, we don’t sell it, and we don’t transfer it to anyone except as needed to run the app or as the law requires. Our use of information received from Google APIs follows the Google API Services User Data Policy, including its Limited Use requirements.",
      ],
    },
    {
      heading: "How long we keep it",
      body: [
        // CONFIRM: retention periods with the lawyer. Sign-in attempt records are
        // only checked over windows of up to an hour but aren't purged yet.
        "We keep your account while you’re a member. After a membership ends, we keep the records we need for billing, tax, safety and legal purposes, then delete or anonymize them. If you ask us to delete your account, we’ll do so except for records we’re required to keep.",
      ],
    },
    {
      heading: "Your choices",
      body: [
        {
          list: [
            "You can see and update your name and photo in the app.",
            "You can cancel your membership in the app at any time.",
            "You can ask us for a copy of your information, to correct it, or to delete it, by emailing info@platform18.org.",
            "You can remove Platform 18’s access to your Google account at any time from your Google Account settings.",
          ],
        },
      ],
    },
    {
      heading: "Security",
      body: [
        "We protect your information with encrypted connections, encryption of door pass codes, hashed passwords, passkeys, a texted code on every password sign-in, and access limited to the people who run Platform 18. No system is perfectly secure, so if you think your account or door pass has been misused, tell us right away in the app or at info@platform18.org.",
      ],
    },
    {
      heading: "Age",
      body: ["Membership is for adults 18 and over. We don’t knowingly collect information from children under 13."],
    },
    {
      heading: "Changes",
      body: [
        "If we change this policy, we’ll update the date at the top. If a change is significant, we’ll tell members in the app or by email before it takes effect.",
      ],
    },
  ],
};

export const terms: LegalDoc = {
  title: "Terms of service",
  updated: "October 6, 2026",
  intro: [
    `These terms apply when you use platform18.org or the Platform 18 members app at members.platform18.org, both run by ${WHO} (“Platform 18”, “we”, “us”). By using them, you agree to these terms. Members also accept the membership agreement and house rules in the app. If those and these terms disagree, the membership agreement and house rules win.`,
  ],
  sections: [
    {
      heading: "Membership",
      body: [
        {
          list: [
            "You must be 18 or older and give accurate information to apply.",
            "Applying doesn’t guarantee membership. Each application is reviewed by a person, and membership starts only when we approve it.",
            "Your membership and door pass are for you alone. You may not share, lend, sell or transfer them.",
          ],
        },
      ],
    },
    {
      heading: "Dues and billing",
      body: [
        {
          list: [
            "Dues are $180 per month, or the price shown when you join, including any trial or discount. We save your card when you apply and charge it only if you’re approved, then every month after that.",
            "We may change prices with 30 days’ notice.",
            "If a payment fails, we’ll retry it and let you know. If it’s still unpaid after 7 days, your access is paused until it’s paid.",
            // CONFIRM: refund terms (also marked CONFIRM in the membership agreement).
            "You can cancel any time in the app. Your access continues until the end of the month you’ve paid for. Unless the law requires otherwise, partial months aren’t refunded.",
            "Payments are processed by Stripe under Stripe’s own terms.",
          ],
        },
      ],
    },
    {
      heading: "Your door pass",
      body: [
        "Your QR door pass is a key. Keep it private, and report a lost phone or a pass you think someone else is using right away in the app, so we can replace it. You’re responsible for entries made with your pass until you report it. We may change or revoke a pass at any time.",
      ],
    },
    {
      heading: "Using the space",
      body: [
        {
          list: [
            "The building is closed to members on Shabbos and Yom Tov, from before candle lighting until after havdalah. Door passes don’t work during those times.",
            "No outside food or drink is permitted in the building.",
            "Meeting rooms are by booking in the app. Please release a booking you won’t use.",
            "Follow the house rules in the app, which cover guests, conduct, safety and more.",
          ],
        },
      ],
    },
    {
      heading: "Using the app and website",
      body: [
        "Please don’t misuse them: no trying to get into accounts or areas that aren’t yours, no interfering with how they work, no automated scraping, and nothing unlawful. Ask gives general answers from Platform 18’s own information. It can be wrong, and it never changes your membership, access or payments. If something matters, check with us.",
      ],
    },
    {
      heading: "Suspension and ending a membership",
      body: [
        "We may suspend or end a membership for breaking these terms or the house rules, for non-payment, for sharing a door pass, or for conduct that puts people or property at risk. You can end yours at any time by cancelling in the app.",
      ],
    },
    {
      heading: "Your safety",
      body: [
        "The space may be open without staff present. You use it at your own risk, as described in the assumption of risk and release in the membership agreement. In an emergency, call 911 first, then let us know.",
      ],
    },
    {
      heading: "Disclaimers and limits",
      body: [
        // CONFIRM: scope and enforceability under Michigan law.
        "The website and app are provided “as is”. We work to keep them and the door system running, but we can’t promise they’ll always be available or error-free. To the fullest extent Michigan law allows, Platform 18 isn’t liable for indirect or consequential losses, and our total liability to you for any claim is limited to the dues you paid in the three months before the claim arose. Nothing in these terms limits liability that can’t be limited by law.",
      ],
    },
    {
      heading: "Content",
      body: [
        "The Platform 18 name, logo, photos and text on the website belong to Platform 18. Please don’t reuse them without permission. You keep ownership of the photo you upload, and you let us use it to run your membership, such as on your pass.",
      ],
    },
    {
      heading: "Privacy",
      body: ["Our privacy policy explains how we handle your information."],
    },
    {
      heading: "Governing law",
      body: [
        // CONFIRM: venue.
        "These terms are governed by the laws of the State of Michigan. Any dispute will be handled in the state or federal courts serving Oakland County, Michigan.",
      ],
    },
    {
      heading: "Changes",
      body: [
        "We may update these terms. We’ll change the date at the top, and for significant changes we’ll tell members in the app or by email before they take effect. Continuing to use the website, app or space after that means you accept the new terms.",
      ],
    },
  ],
};
